import React from 'react';
import {
  TEXT_DARK, TEXT_BODY, TEXT_SECONDARY, TEXT_MUTED,
  FONT_HEADING, BORDER_LIGHT,
  STATUS_COLORS as DESIGN_STATUS_COLORS,
  STATUS_BG as DESIGN_STATUS_BG,
} from './designTokens';

export interface SeatData {
  id: string;
  compositeId: string;
  seatNumber: number;
  status: string;
  guestId: string | null;
  guestName?: string;
  mealPreference: string | null;
  accessibilityNotes: string | null;
  checkedIn: boolean;
  checkedInAt: string | null;
  blockType: string | null;
  blockReason: string | null;
  holdExpiration: string | null;
  holdReason: string | null;
  reservedGroup: string | null;
  familyGroupLocked?: boolean;
}

interface SeatCardProps {
  seat: SeatData;
  onAssign?: (seatId: string) => void;
  onUnassign?: (seatId: string) => void;
  onBlock?: (seatId: string) => void;
  onHold?: (seatId: string) => void;
  onRelease?: (seatId: string) => void;
}

const STATUS_COLORS: Record<string, string> = {
  Available: DESIGN_STATUS_COLORS.Available,
  Assigned: DESIGN_STATUS_COLORS.Assigned,
  Held: DESIGN_STATUS_COLORS.Held,
  Blocked: DESIGN_STATUS_COLORS.Blocked,
  CheckedIn: DESIGN_STATUS_COLORS.CheckedIn,
};

const STATUS_BG: Record<string, string> = {
  Available: DESIGN_STATUS_BG.Available,
  Assigned: DESIGN_STATUS_BG.Assigned,
  Held: DESIGN_STATUS_BG.Held,
  Blocked: DESIGN_STATUS_BG.Blocked,
  CheckedIn: DESIGN_STATUS_BG.CheckedIn,
};

const styles: Record<string, React.CSSProperties> = {
  card: {
    padding: '0.6rem 0.75rem',
    borderRadius: '6px',
    border: `1px solid ${BORDER_LIGHT}`,
    marginBottom: '0.5rem',
    transition: 'box-shadow 0.15s',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.3rem',
  },
  seatId: {
    fontSize: '0.85rem',
    fontFamily: FONT_HEADING,
    fontWeight: 700,
    color: TEXT_DARK,
  },
  statusBadge: {
    fontSize: '0.65rem',
    fontWeight: 600,
    padding: '0.1rem 0.4rem',
    borderRadius: '3px',
    color: '#fff',
    textTransform: 'uppercase' as const,
  },
  guestName: {
    fontSize: '0.82rem',
    color: TEXT_BODY,
    marginBottom: '0.2rem',
    fontWeight: 500,
  },
  mealPref: {
    fontSize: '0.72rem',
    color: TEXT_SECONDARY,
    fontStyle: 'italic',
  },
  actions: {
    display: 'flex',
    gap: '0.35rem',
    marginTop: '0.4rem',
    flexWrap: 'wrap',
  },
  actionBtn: {
    fontSize: '0.7rem',
    padding: '0.2rem 0.5rem',
    borderRadius: '3px',
    border: '1px solid #ccc',
    backgroundColor: '#fff',
    cursor: 'pointer',
    fontWeight: 500,
    transition: 'background-color 0.15s',
  },
  hardBlockBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.2rem',
    fontSize: '0.65rem',
    fontWeight: 700,
    color: '#b71c1c',
    backgroundColor: '#ffebee',
    padding: '0.1rem 0.35rem',
    borderRadius: '3px',
    marginLeft: '0.35rem',
  },
  expiryWarning: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.2rem',
    fontSize: '0.65rem',
    fontWeight: 600,
    color: '#e65100',
    backgroundColor: '#fff3e0',
    padding: '0.1rem 0.35rem',
    borderRadius: '3px',
    marginTop: '0.25rem',
  },
  blockInfo: {
    fontSize: '0.7rem',
    color: TEXT_MUTED,
    marginTop: '0.15rem',
  },
};

function isExpiringSoon(holdExpiration: string | null): boolean {
  if (!holdExpiration) return false;
  const expiry = new Date(holdExpiration);
  const now = new Date();
  const hoursUntilExpiry = (expiry.getTime() - now.getTime()) / (1000 * 60 * 60);
  return hoursUntilExpiry > 0 && hoursUntilExpiry <= 24;
}

export function SeatCard({ seat, onAssign, onUnassign, onBlock, onHold, onRelease }: SeatCardProps) {
  const isHardBlock = seat.blockType === 'Hard';
  const expiringSoon = isExpiringSoon(seat.holdExpiration);

  return (
    <div
      style={{
        ...styles.card,
        backgroundColor: STATUS_BG[seat.status] || '#fff',
        borderLeft: `4px solid ${STATUS_COLORS[seat.status] || '#999'}`,
        ...(isHardBlock ? { borderLeft: '4px solid #b71c1c' } : {}),
      }}
      role="article"
      aria-label={`Seat ${seat.compositeId}, status: ${seat.status}${seat.guestName ? `, assigned to ${seat.guestName}` : ''}`}
    >
      <div style={styles.header}>
        <span style={styles.seatId}>
          {seat.compositeId}
          {isHardBlock && (
            <span style={styles.hardBlockBadge} title="Hard Block - Admin only">
              🔒 Hard
            </span>
          )}
        </span>
        <span
          style={{
            ...styles.statusBadge,
            backgroundColor: STATUS_COLORS[seat.status] || '#999',
          }}
        >
          {seat.status}
        </span>
      </div>

      {seat.guestName && (
        <div style={styles.guestName}>
          {seat.guestName}
          {seat.familyGroupLocked && (
            <span title="Member of a locked family group" style={{ marginLeft: '0.3rem', fontSize: '0.72rem' }}>🔒</span>
          )}
        </div>
      )}

      {seat.mealPreference && (
        <div style={styles.mealPref}>🍽 {seat.mealPreference}</div>
      )}

      {seat.blockReason && (
        <div style={styles.blockInfo}>Reason: {seat.blockReason}</div>
      )}

      {seat.holdReason && (
        <div style={styles.blockInfo}>Hold: {seat.holdReason}</div>
      )}

      {seat.reservedGroup && (
        <div style={styles.blockInfo}>Group: {seat.reservedGroup}</div>
      )}

      {expiringSoon && (
        <div style={styles.expiryWarning} role="alert" aria-live="polite">
          ⏰ Expiring within 24h
        </div>
      )}

      <div style={styles.actions}>
        {seat.status === 'Available' && (
          <>
            <button
              style={{ ...styles.actionBtn, color: '#1565c0', borderColor: '#1565c0' }}
              onClick={() => onAssign?.(seat.id)}
              aria-label={`Assign guest to seat ${seat.compositeId}`}
            >
              Assign
            </button>
            <button
              style={{ ...styles.actionBtn, color: '#616161', borderColor: '#616161' }}
              onClick={() => onBlock?.(seat.id)}
              aria-label={`Block seat ${seat.compositeId}`}
            >
              Block
            </button>
            <button
              style={{ ...styles.actionBtn, color: '#f57c00', borderColor: '#f57c00' }}
              onClick={() => onHold?.(seat.id)}
              aria-label={`Hold seat ${seat.compositeId}`}
            >
              Hold
            </button>
          </>
        )}
        {seat.status === 'Assigned' && (
          <button
            style={{ ...styles.actionBtn, color: '#d32f2f', borderColor: '#d32f2f' }}
            onClick={() => onUnassign?.(seat.id)}
            aria-label={`Unassign guest from seat ${seat.compositeId}`}
          >
            Unassign
          </button>
        )}
        {seat.status === 'Held' && (
          <button
            style={{ ...styles.actionBtn, color: '#2e7d32', borderColor: '#2e7d32' }}
            onClick={() => onRelease?.(seat.id)}
            aria-label={`Release hold on seat ${seat.compositeId}`}
          >
            Release
          </button>
        )}
      </div>
    </div>
  );
}
